//*****************************************************
// All the configuration contracts between the app and RevenueCat
// documented in /docs/paywall.md for marketing team
//*****************************************************

import { AccountType, type OfferingPromoMetadata } from "@um/contracts";

/**
 * RevenueCat public API keys + selection logic live in `@um/contracts`
 * (`revenueCatKeys.ts`) so the web SDK, native SDK, and backend all share one
 * source. Re-exported here for existing `@um/ui/services/revenueCat` importers.
 */
export {
  REVENUECAT_TEST_API_KEY_PROD,
  REVENUECAT_TEST_API_KEY_STAGING,
  REVENUECAT_WEB_BILLING_API_KEY_PROD,
  REVENUECAT_WEB_BILLING_API_KEY_PROD_SANDBOX,
  REVENUECAT_WEB_BILLING_API_KEY_STAGING_SANDBOX,
  REVENUECAT_STRIPE_API_KEY_PROD,
  REVENUECAT_STRIPE_API_KEY_PROD_SANDBOX,
  REVENUECAT_Apple_API_KEY,
  REVENUECAT_Google_API_KEY,
  getRevenueCatPublicApiKey,
  asRevenueCatClientPlatform,
  type RevenueCatClientPlatform,
} from "@um/contracts";
export type { OfferingPromoMetadata } from "@um/contracts";

/**
 * The two placements involved in the routine paywall timing experiment.
 *
 * The experiment lives at `ROUTINE_PAYWALL_CLICK_PLACEMENT`. RC's targeting at
 * `ROUTINE_PAYWALL_AUTH_LINK_PLACEMENT` excludes users assigned to variant B of that
 * experiment, so each user sees the paywall at exactly one of the two sites.
 *
 * The app prefetches both placements on RC ready so the variant assignment is locked
 * in and the variant tag (offering identifier of whichever placement returned an
 * offering) is reported to GA / Firestore / RC subscriber attributes early in the
 * session. See `docs/paywall.md` §7.4.
 */
export const ROUTINE_PAYWALL_AUTH_LINK_PLACEMENT =
  "onboarding/post-character-creation/auth-link";
export const ROUTINE_PAYWALL_CLICK_PLACEMENT = "select-routine-click";

/**
 * Flow `stepId` for {@link ROUTINE_PAYWALL_CLICK_PLACEMENT}. HybridFlowPage builds the
 * step-mount placement as `…/routine-selection/select-routine` (no `/click` suffix). RevenueCat
 * matches **exact** placement ids — those two strings are different — so we skip the generic
 * step-mount paywall on this step and only query RC from `handleRoutineButtonClick` using
 * {@link ROUTINE_PAYWALL_CLICK_PLACEMENT}.
 */
export const ROUTINE_PAYWALL_CLICK_ONBOARDING_STEP_ID = "select-routine";

/** Placements throughout the app configured in RevenueCat (Targeting / Paywalls). */
// This also includes all the onboarding routes
export type PaywallPlacementIdentifier =
  | "character-creation"
  | "crew/job-edit"
  | "understand"
  | typeof ROUTINE_PAYWALL_CLICK_PLACEMENT
  | typeof ROUTINE_PAYWALL_AUTH_LINK_PLACEMENT;

/** Entitlements that control access to features in the app. */
export type EntitlementIdentifier = "basic" | "clinical_support";

/**
 * Offering metadata keys we read in-app (RevenueCat dashboard → Offering → Metadata).
 * This allows the offering to modify the behavior of the app.
 */
export interface OfferingMetadata {
  /** Any truthy value (e.g. boolean `true`, non-empty string) → soft / dismissible paywall. */
  soft?: string | boolean;
  /**
   * Free-access promotional grant configuration — **consumed by the backend,
   * not the app**. At account creation, `getOrCreateAccount` resolves the new
   * account's current (no-placement) offering via RevenueCat's REST API and,
   * when it carries this object, grants the entitlements as promotional
   * entitlements — the same mechanism as wholesaler code redemption
   * (`docs/paywall.md` §11.4). Type lives in `@um/contracts` (shared with the
   * backend); formats mirror the wholesaler Stripe product metadata (§11.3).
   */
  promo?: OfferingPromoMetadata;
}

/**
 * Runtime paywall template variables.
 * Allows the app to customize the paywall template.
 */
export interface PaywallVariables {
  dismissCount: number;
}

/**
 * App-defined RevenueCat subscriber attributes.
 * Used for targeting accounts with the correct offering.
 */
export interface SubscriberAttributes {
  paywallClass?: "new"; // accounts created after paywall was released
  /**
   * Identifier of the offering RC served at the routine paywall experiment placement
   * (see `ROUTINE_PAYWALL_EXPERIMENT_PLACEMENT`). Used as the variant tag in funnels
   * and follow-up RC rules. Stamped once on RC ready and re-stamped when marketing
   * activates a new experiment that resolves to a different offering.
   */
  routinePaywallExperiment?: string;
  /**
   * Routine-menu experiment variant ("control" | "juicy") from the
   * `RoutineMenuVariant` Remote Config parameter. Stamped once per session by
   * `services/experiments.ts` so RevenueCat charts can segment trials/paid by
   * arm (the who-pays readout Firebase A/B can't provide).
   */
  routineMenuVariant?: string;
  daysWithApp: "1" | "2" | "3" | "4" | "5" | "6" | "7" | "7-14" | "15+";
  isInternal: "true" | "false";
  accountType: AccountType;
  testResourceGroup?: string;
  utm_source?: string;
  utm_campaign?: string;
  utm_id?: string;
  utm_medium?: string;
}
